<script setup lang="ts">
import { BACKEND_URL } from '@/services/apiClient';
import { computed, ref, onUpdated } from 'vue';

const props = defineProps({
  avatarUrl: {
    type: String
  },
  userName: {
    type: String
  },
  userEmail: {
    type: String
  },
  size: {
    type: Number,
    default: 50
  },
  showName: {
    type: Boolean,
    default: true
  },
  loading: {
    type: Boolean,
    default: false
  }
});

const getInitials = (userName?: string) => {
  const name = userName?.split(' ');
  if (name) {
    return `${name[0].charAt(0)}${name[1] ? name[1].charAt(0) : ''}`;
  } else {
    return 'TS';
  }
};

const computedSize = computed(() => `${props.size}px`);
const computedAvatarUrl = computed(() => {
  return `${BACKEND_URL}${props.avatarUrl}`;
});
const computedLabel = computed(() => getInitials(props.userName));

const avatarLoadError = ref(false);

const onAvatarError = () => {
  // todo, Set the default text
  avatarLoadError.value = true;
};

const handleImageLoad = () => {
  avatarLoadError.value = false;
};

onUpdated(() => {
  if (props.avatarUrl && avatarLoadError.value === true) {
    checkImageExists(computedAvatarUrl.value);
  }
});

const checkImageExists = (imageUrl: string) => {
  const img = new Image();
  img.onload = () => {
    avatarLoadError.value = false;
  };
  img.onerror = () => {
    avatarLoadError.value = true;
  };
  img.src = imageUrl;
};

</script>

<template>
    <div class="user-avatar">
        <div class="avatar" v-if="!loading">
          <img :src="computedAvatarUrl"
            :key="avatarUrl"
            v-if="!avatarLoadError"
            @load="handleImageLoad"
            @error="onAvatarError"/>

          <div class="cicle-label" v-if="avatarLoadError">{{ computedLabel }}</div>
        </div>
        <div v-else-if="loading">Loading...</div>
        <div class="details">
          <span v-if="showName" class="name">{{ userName }}</span>
          <span v-if="userEmail" class="email">{{ userEmail }}</span>
        </div>
    </div>
</template>

<style scoped lang="scss">
.user-avatar{
    display: flex;
    align-items: center;
}
.avatar {
    margin-right: 8px;
    img {
        width: v-bind(computedSize);
        border-radius: 50%;
    }
}
.cicle-label {
    width: v-bind(computedSize);
    height: v-bind(computedSize);
    background-color: #ece9fc; 
    color: #2a1261;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
}
.details {
  display: flex;
  flex-direction: column;
}
.name {
  font-weight: 500;
  line-height: 1em;
}
.email {
  font-weight: 300;
}
</style>
